import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, Plus, RefreshCw, Search, Wand2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildPromptCreationDraftFromAssistantResult,
  createDefaultPromptCreationDraft,
  PromptCreationDraft,
} from '../features/prompt-creation/draft';
import { useEngineCatalog } from '../hooks/useEngineCatalog';
import { commitOwnedSearch } from '../navigation/historyState';
import { parseFlagParam } from '../navigation/urlCodec';
import { apiService, EngineKind, getApiErrorMessage, Prompt } from '../services/api';
import { normalizeRealtimeEngineConfig } from '../features/realtime/config';
import { useTabState } from '../contexts/TabStateContext';
import type { EngineEditorTab } from '../features/workflows/presentation';
import { isSystemPrompt } from '../utils/systemPrompts';
import PromptCard from './PromptCard';
import PromptDetailSidebar from './PromptDetailSidebar';
import './AssistantPanel.css';
import './EngineGallery.css';

interface EngineGalleryProps {
  kind?: EngineKind;
  selectedPromptId: string;
  onSelect: (prompt: Prompt) => void;
  disabled?: boolean;
  isActive?: boolean;
  manageEditorLinks?: boolean;
}

const createTemporaryPrompt = (draft: PromptCreationDraft): Prompt => ({
  prompt_id: `temp_${Date.now()}`,
  is_realtime: draft.isRealtime === true,
  realtime_config: draft.realtimeConfig,
  name: draft.name || 'New Extraction Engine',
  description: draft.description,
  prompt_text: '',
  json_schema: { type: 'object', properties: {}, required: [], additionalProperties: false },
  semantic_indexing: { disabled_segment_fields: [], disabled_video_level_fields: [] },
  created_at: new Date().toISOString(),
  user_id: 'current_user',
  is_active: true,
});

const EngineGallery: React.FC<EngineGalleryProps> = ({ selectedPromptId, onSelect, disabled = false, isActive = true, manageEditorLinks = true, kind = 'video' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const libraryId = useId();
  const { prompts, builtInPrompts, customPrompts, defaultPromptId, isLoading, error, refresh, upsert } = useEngineCatalog({ kind });
  const { enginesTabState, setEnginesTabState, enginesTabStateReady } = useTabState();
  const [realtimeDraft, setRealtimeDraft] = useState<PromptCreationDraft | null>(null);
  const activeCreateDraft = kind === 'realtime' ? realtimeDraft : enginesTabState.promptCreateDraft;
  const setCatalogDraft = useCallback((draft: PromptCreationDraft | null) => {
    if (kind === 'realtime') setRealtimeDraft(draft);
    else setEnginesTabState({ promptCreateDraft: draft });
  }, [kind, setEnginesTabState]);
  const [searchTerm, setSearchTerm] = useState(() => manageEditorLinks ? new URLSearchParams(location.search).get('q') || '' : '');
  const [detailPrompt, setDetailPrompt] = useState<Prompt | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [libraryTab, setLibraryTab] = useState<'built-in' | 'custom'>(kind === 'realtime' ? 'custom' : 'built-in');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const generationAbortRef = useRef<AbortController | null>(null);
  const [editorTab, setEditorTab] = useState<EngineEditorTab>('extraction');
  const [expandedPromptIds, setExpandedPromptIds] = useState<Set<string>>(new Set());
  const handledEditorLinkRef = useRef<string | null>(null);
  const lastKnownCreateDraftRef = useRef<PromptCreationDraft | null>(activeCreateDraft);

  useEffect(() => {
    lastKnownCreateDraftRef.current = activeCreateDraft;
  }, [activeCreateDraft]);

  useEffect(() => {
    setIsGenerating(false);
    setGenerationError(null);
    return () => {
      generationAbortRef.current?.abort();
      generationAbortRef.current = null;
    };
  }, [isActive, kind]);

  const selectedPrompt = prompts.find((prompt) => prompt.prompt_id === selectedPromptId);
  const selectedLibrary = selectedPrompt ? (isSystemPrompt(selectedPrompt) ? 'built-in' : 'custom') : null;
  useEffect(() => {
    if (selectedLibrary) setLibraryTab(selectedLibrary);
  }, [selectedPromptId, selectedLibrary]);

  const canChangeEditor = useCallback(() => !isSaving && (
    !detailPrompt || isCreating || !hasUnsavedChanges
    || window.confirm(`Discard unsaved changes to "${detailPrompt.name}"?`)
  ), [detailPrompt, hasUnsavedChanges, isCreating, isSaving]);

  const openPrompt = useCallback((prompt: Prompt, stage: EngineEditorTab = 'extraction') => {
    if (detailPrompt?.prompt_id !== prompt.prompt_id && !canChangeEditor()) return false;
    if (isSaving) return false;
    setIsCreating(false);
    if (detailPrompt?.prompt_id !== prompt.prompt_id) {
      setHasUnsavedChanges(false);
      setDetailPrompt(prompt);
    }
    setEditorTab(stage);
    setNotice(null);
    return true;
  }, [canChangeEditor, detailPrompt?.prompt_id, isSaving]);

  const selectPrompt = (prompt: Prompt) => {
    if (!openPrompt(prompt, 'extraction')) return;
    onSelect(prompt);
  };

  const openCreatePrompt = useCallback((sourceDraft?: PromptCreationDraft, stage: EngineEditorTab = 'extraction') => {
    if (!canChangeEditor()) return;
    const nextDraft = sourceDraft || activeCreateDraft
      || lastKnownCreateDraftRef.current || { ...createDefaultPromptCreationDraft(), ...(kind === 'realtime' ? { isRealtime: true, realtimeConfig: normalizeRealtimeEngineConfig() } : {}) };
    lastKnownCreateDraftRef.current = nextDraft;
    setCatalogDraft(nextDraft);
    setDetailPrompt(createTemporaryPrompt(nextDraft));
    setEditorTab(stage);
    setIsCreating(true);
    setNotice(null);
  }, [canChangeEditor, activeCreateDraft, kind, setCatalogDraft]);

  const closeSidebar = useCallback(() => {
    setDetailPrompt(null);
    setIsCreating(false);
  }, []);

  // Legacy engine links open the editor once. The host owns execution selection.
  useEffect(() => {
    if (!manageEditorLinks || !isActive || !enginesTabStateReady || isLoading || (error && prompts.length === 0)) return;
    const params = new URLSearchParams(location.search);
    const createRequested = parseFlagParam(params.get('new'));
    const editRequested = parseFlagParam(params.get('edit'));
    if ((!createRequested && !editRequested) || handledEditorLinkRef.current === location.key) return;
    handledEditorLinkRef.current = location.key;
    if (createRequested) {
      openCreatePrompt();
    } else {
      const prompt = prompts.find((candidate) => candidate.prompt_id === params.get('prompt'));
      if (prompt) openPrompt(prompt);
      else setNotice('That extraction engine is no longer available.');
    }
    commitOwnedSearch({ location, navigate, ownedKeys: ['new', 'edit'], updates: {}, mode: 'replace' });
  }, [manageEditorLinks, isActive, enginesTabStateReady, error, isLoading, location, navigate, openCreatePrompt, openPrompt, prompts]);

  const handlePromptSave = useCallback((savedPrompt: Prompt) => {
    upsert(savedPrompt);
    setDetailPrompt(savedPrompt);
    setIsCreating(false);
    setLibraryTab('custom');
    setSearchTerm('');
    if (isCreating) {
      lastKnownCreateDraftRef.current = null;
      setCatalogDraft(null);
    }
    onSelect(savedPrompt);
  }, [isCreating, onSelect, setCatalogDraft, upsert]);

  const updateCreateDraft = useCallback((draft: PromptCreationDraft) => {
    lastKnownCreateDraftRef.current = draft;
    setCatalogDraft(draft);
  }, [setCatalogDraft]);

  const discardCreateDraft = useCallback(() => {
    lastKnownCreateDraftRef.current = null;
    setCatalogDraft(null);
  }, [setCatalogDraft]);

  const createDisabled = disabled || isGenerating || isCreating || isSaving || !enginesTabStateReady;
  const assistantInstruction = activeCreateDraft?.assistantPrompt || '';
  const generateCustomEngine = async (event: React.FormEvent) => {
    event.preventDefault();
    const instruction = assistantInstruction.trim();
    if (createDisabled || !isActive || !instruction || generationAbortRef.current) return;
    const controller = new AbortController();
    generationAbortRef.current = controller;
    setIsGenerating(true);
    setGenerationError(null);
    try {
      const generated = await apiService.generatePromptDraft(instruction, {}, controller.signal);
      if (controller.signal.aborted) return;
      const draft = buildPromptCreationDraftFromAssistantResult(
        generated,
        lastKnownCreateDraftRef.current || createDefaultPromptCreationDraft(),
        instruction,
      );
      openCreatePrompt(draft, 'extraction');
    } catch (error) {
      if (!controller.signal.aborted) {
        setGenerationError(getApiErrorMessage(error, 'Unable to generate an engine. Please try again.'));
      }
    } finally {
      if (generationAbortRef.current === controller) {
        generationAbortRef.current = null;
        setIsGenerating(false);
      }
    }
  };

  const normalizedSearch = searchTerm.trim().toLocaleLowerCase();
  const visiblePrompts = useMemo(() => (
    (libraryTab === 'built-in' ? builtInPrompts : customPrompts).filter((prompt) => (
      !normalizedSearch || prompt.name.toLocaleLowerCase().includes(normalizedSearch)
      || prompt.description.toLocaleLowerCase().includes(normalizedSearch)
    ))
  ), [builtInPrompts, customPrompts, libraryTab, normalizedSearch]);

  const updateExpanded = (promptId: string, expanded: boolean) => {
    setExpandedPromptIds((current) => {
      const next = new Set(current);
      if (expanded) next.add(promptId);
      else next.delete(promptId);
      return next;
    });
  };
  const setAllExpanded = (expanded: boolean) => {
    setExpandedPromptIds((current) => {
      const next = new Set(current);
      visiblePrompts.forEach((prompt) => {
        if (expanded) next.add(prompt.prompt_id);
        else next.delete(prompt.prompt_id);
      });
      return next;
    });
  };
  const allExpanded = visiblePrompts.length > 0 && visiblePrompts.every((prompt) => expandedPromptIds.has(prompt.prompt_id));

  return (
    <div className="engine-gallery">
      <section className="engines-library" aria-label="Engine library">
        <header className="engines-library-header">
          <div className="engines-library-tabs" role="tablist" aria-label="Engine library">
            {([
              { id: 'built-in', label: 'Built-in engines', count: builtInPrompts.length },
              { id: 'custom', label: 'My engines', count: customPrompts.length },
            ] as const).map((tab) => (
              <button
                key={tab.id}
                id={`${libraryId}-tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={libraryTab === tab.id}
                aria-controls={`${libraryId}-panel-${tab.id}`}
                className={libraryTab === tab.id ? 'active' : ''}
                onClick={() => setLibraryTab(tab.id)}
              >{tab.label}<span>{tab.count}</span></button>
            ))}
          </div>
          <div className="engines-library-controls">
            <label className="engines-search">
              <Search size={16} aria-hidden="true" />
              <span className="engines-visually-hidden">Search extraction engines</span>
              <input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Find an engine..." />
            </label>
            <button type="button" className="engines-refresh-button" onClick={() => void refresh()} disabled={isLoading} title="Refresh engines" aria-label="Refresh engines">
              <RefreshCw size={16} className={isLoading ? 'engines-spinning' : undefined} aria-hidden="true" />
            </button>
            <div className="engines-expand-controls">
              <button type="button" onClick={() => setAllExpanded(true)} disabled={visiblePrompts.length === 0 || allExpanded} title="Expand all engines" aria-label="Expand all engines"><ChevronDown size={15} aria-hidden="true" /></button>
              <button type="button" onClick={() => setAllExpanded(false)} disabled={!visiblePrompts.some((prompt) => expandedPromptIds.has(prompt.prompt_id))} title="Collapse all engines" aria-label="Collapse all engines"><ChevronUp size={15} aria-hidden="true" /></button>
            </div>
          </div>
        </header>

        {notice && (
          <div className="engines-notice" role="status"><span>{notice}</span><button type="button" onClick={() => setNotice(null)} aria-label="Dismiss notice">×</button></div>
        )}
        {error && (
          <div className="engines-error" role="alert"><AlertCircle size={18} aria-hidden="true" /><span>{error}</span><button type="button" onClick={() => void refresh()}>Try again</button></div>
        )}

        {isLoading && prompts.length === 0 ? (
          <div className="engines-loading" role="status"><RefreshCw size={24} className="engines-spinning" aria-hidden="true" /><span>Loading extraction engines...</span></div>
        ) : (
          <div className="engines-library-content" role="tabpanel" id={`${libraryId}-panel-${libraryTab}`} aria-labelledby={`${libraryId}-tab-${libraryTab}`}>
            <div id={`${libraryId}-cards`} className={`engines-grid${allExpanded ? ' all-expanded' : ''}`}>
              <form className="engines-create-card" onSubmit={generateCustomEngine} aria-label="Create a custom engine" aria-busy={isGenerating}>
                <strong>Create a custom engine</strong>
                <div className="engines-create-prompt">
                  <label className="prompt-assistant-label" htmlFor={`${libraryId}-assistant`}><Wand2 size={15} aria-hidden="true" /> AI assistant</label>
                  <textarea id={`${libraryId}-assistant`} className="prompt-assistant-textarea"
                    aria-label="What would you like to extract or learn from your videos?"
                    placeholder="What would you like to extract or learn from your videos?"
                    value={assistantInstruction} maxLength={12000} disabled={createDisabled}
                    onChange={(event) => {
                      setGenerationError(null);
                      updateCreateDraft({
                        ...(activeCreateDraft || createDefaultPromptCreationDraft()),
                        ...(kind === 'realtime' ? { isRealtime: true, realtimeConfig: activeCreateDraft?.realtimeConfig || normalizeRealtimeEngineConfig() } : {}),
                        assistantPrompt: event.target.value,
                      });
                    }} />
                  <button type="submit" className="prompt-assistant-generate-btn" disabled={createDisabled || !assistantInstruction.trim()}>
                    {isGenerating ? <RefreshCw size={15} className="engines-spinning" aria-hidden="true" /> : <Plus size={15} aria-hidden="true" />}
                    {isGenerating ? 'Generating…' : 'Generate a custom engine'}
                  </button>
                </div>
                {generationError && <div className="engines-create-error" role="alert">{generationError}</div>}
                <button type="button" className="engines-create-manual" onClick={() => openCreatePrompt()} disabled={createDisabled}>
                  {activeCreateDraft ? 'Continue draft' : 'Create manually'}
                </button>
              </form>
              {visiblePrompts.map((prompt) => (
                <PromptCard
                  key={prompt.prompt_id}
                  prompt={prompt}
                  presentation="workflow"
                  isDefault={prompt.prompt_id === defaultPromptId}
                  expanded={expandedPromptIds.has(prompt.prompt_id)}
                  onExpandedChange={(expanded) => updateExpanded(prompt.prompt_id, expanded)}
                  isSelected={selectedPromptId === prompt.prompt_id}
                  onClick={() => selectPrompt(prompt)}
                  disabled={disabled || isSaving || isGenerating}
                />
              ))}
            </div>
            {visiblePrompts.length === 0 && (
              <p className="engines-empty-message" role="status">{normalizedSearch ? 'No engines match your search.' : libraryTab === 'custom' ? 'Create an engine or clone a built-in to make it your own.' : 'No built-in engines are available. Refresh to try again.'}</p>
            )}
          </div>
        )}
      </section>

      <PromptDetailSidebar
        prompt={detailPrompt}
        engineKind={kind}
        isOpen={Boolean(detailPrompt)}
        isVisible={isActive}
        onClose={closeSidebar}
        onSave={handlePromptSave}
        isCreating={isCreating}
        createDraft={isCreating ? activeCreateDraft : null}
        onCreateDraftChange={updateCreateDraft}
        onDiscardCreateDraft={discardCreateDraft}
        readOnly={Boolean(detailPrompt && isSystemPrompt(detailPrompt))}
        initialTab={editorTab}
        onTabChange={setEditorTab}
        onUnsavedChangesChange={setHasUnsavedChanges}
        onSavingChange={setIsSaving}
      />
    </div>
  );
};

export default EngineGallery;
